/**
 * Reliable teardown for an open Razorpay Standard Checkout.
 *
 * Why this exists: `rzp.close()` is a no-op while checkout still considers a
 * payment in flight. Our success handler fires exactly at that moment (the
 * gateway calls `handler` the instant it has a payment id, before it has torn
 * its own UI down), so a single `close()` call is silently dropped and the pay
 * sheet repaints itself over whatever page we navigated to — the "pay popup
 * comes back after a successful payment" report. Because checkout mounts its
 * container on document.body, client-side navigation cannot remove it either.
 *
 * So: retry the polite close a few times, then remove the container outright
 * and restore the scroll lock checkout puts on <body>.
 */

import { resetRazorpaySdk } from "./razorpayLoader";

const RETRY_DELAYS_MS = [120, 350, 800, 1600, 2600];

type RzpLike = { close?: () => void };

const openCheckouts = new Set<RzpLike>();
let razorpayOwnedScrollLock = false;

/** Removes checkout's body-level DOM and undoes its scroll lock. */
export function removeRazorpayDom(): void {
  if (typeof document === "undefined") return;
  const nodes = document.querySelectorAll<HTMLElement>(
    ".razorpay-container, .razorpay-backdrop, .razorpay-checkout-frame",
  );
  const hadRazorpayUi = nodes.length > 0 || razorpayOwnedScrollLock || openCheckouts.size > 0;
  nodes.forEach((node) => node.remove());
  if (!hadRazorpayUi) return;
  // The SDK still points at the frame we just removed; load a fresh copy next time.
  if (nodes.length > 0) resetRazorpaySdk();
  // Checkout sets these inline while open; leaving them freezes our page.
  document.body.style.overflow = "";
  document.body.style.position = "";
  document.documentElement.style.overflow = "";
  razorpayOwnedScrollLock = false;
}

/**
 * Closes a checkout instance for good.
 *
 * Safe to call from a success handler, an unmount cleanup, or both.
 */
export function forceCloseRazorpay(rzp?: { close?: () => void } | null): void {
  const attempt = () => {
    try {
      rzp?.close?.();
    } catch {
      /* already gone */
    }
  };
  attempt();
  if (typeof window === "undefined") return;
  RETRY_DELAYS_MS.forEach((ms) => window.setTimeout(attempt, ms));
  // Last resort once the retries have had their chance.
  window.setTimeout(removeRazorpayDom, RETRY_DELAYS_MS[RETRY_DELAYS_MS.length - 1] + 400);
}

/* -------------------------------------------------------------------------- */
/* Registry of open checkouts, so leaving a page can close them at once.      */
/* -------------------------------------------------------------------------- */

/** Call right after `new window.Razorpay(...)`.open(). */
export function registerRazorpay(rzp: RzpLike | null | undefined): void {
  if (rzp) {
    openCheckouts.add(rzp);
    razorpayOwnedScrollLock = true;
  }
}

/** Call once an instance is known to be gone (e.g. from `ondismiss`). */
export function unregisterRazorpay(rzp: RzpLike | null | undefined): void {
  if (!rzp) return;
  openCheckouts.delete(rzp);
  if (
    openCheckouts.size === 0 &&
    typeof document !== "undefined" &&
    !document.querySelector(".razorpay-container, .razorpay-backdrop, .razorpay-checkout-frame")
  ) {
    razorpayOwnedScrollLock = false;
  }
}

/**
 * Immediate teardown, for "the buyer left this page" moments (unmount, route
 * change, Back to home). Unlike `forceCloseRazorpay`, this does not wait out
 * the retry ladder: it closes every known instance, strips checkout's
 * body-level DOM synchronously, and repeats on the next frames to catch a
 * late repaint. Handles are removed from the active registry immediately, but
 * retained in this closure so delayed gateway repaints are still closed.
 */
export function closeRazorpayNow(extra?: RzpLike | null): void {
  if (extra) openCheckouts.add(extra);
  const instances = Array.from(openCheckouts);
  const closeAll = () => {
    instances.forEach((rzp) => {
      try {
        rzp.close?.();
      } catch {
        /* already gone */
      }
    });
    removeRazorpayDom();
  };
  closeAll();
  instances.forEach((rzp) => openCheckouts.delete(rzp));
  if (typeof window !== "undefined") {
    [60, 200, 600].forEach((ms) => window.setTimeout(closeAll, ms));
  }
}
